import { useCallback, useEffect, useState } from 'react';
import {
  platformUserService,
  PlatformAccount,
  PlatformUserFilters,
  CreatePlatformRoleDto,
  CreatePlatformUserDto,
  UpdatePlatformRoleDto,
  UpdatePlatformUserDto,
} from '../services/platform-user.service';
import type {
  PlatformPermissionCatalogGroup,
  PlatformRoleCatalogItem,
} from '@erpgold/shared';

interface UsePlatformUsersReturn {
  users: PlatformAccount[];
  loading: boolean;
  error: Error | null;
  filters: PlatformUserFilters;
  setFilters: (filters: PlatformUserFilters) => void;
  meta: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  roleCatalog: PlatformRoleCatalogItem[];
  permissionCatalog: PlatformPermissionCatalogGroup[];
  refetch: () => void;
  createUser: (payload: CreatePlatformUserDto) => Promise<PlatformAccount>;
  updateUser: (id: string, payload: UpdatePlatformUserDto) => Promise<PlatformAccount>;
  deleteUser: (id: string) => Promise<void>;
  createRole: (payload: CreatePlatformRoleDto) => Promise<PlatformRoleCatalogItem>;
  updateRole: (id: string, payload: UpdatePlatformRoleDto) => Promise<PlatformRoleCatalogItem>;
  deleteRole: (id: string) => Promise<void>;
}

export const usePlatformUsers = (
  initialFilters: PlatformUserFilters = {},
): UsePlatformUsersReturn => {
  const [users, setUsers] = useState<PlatformAccount[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [filters, setFilters] = useState<PlatformUserFilters>({
    page: 1,
    limit: 20,
    ...initialFilters,
  });
  const [meta, setMeta] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });
  const [roleCatalog, setRoleCatalog] = useState<PlatformRoleCatalogItem[]>([]);
  const [permissionCatalog, setPermissionCatalog] = useState<PlatformPermissionCatalogGroup[]>([]);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [usersResponse, roles, permissions] = await Promise.all([
        platformUserService.getUsers(filters),
        platformUserService.getRoleCatalog(),
        platformUserService.getPermissionCatalog(),
      ]);

      setUsers(usersResponse.data);
      setMeta(usersResponse.meta);
      setRoleCatalog(roles);
      setPermissionCatalog(permissions);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Không thể tải dữ liệu tài khoản'));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const createUser = async (payload: CreatePlatformUserDto) => {
    const created = await platformUserService.createUser(payload);
    await fetchUsers();
    return created;
  };

  const updateUser = async (id: string, payload: UpdatePlatformUserDto) => {
    const updated = await platformUserService.updateUser(id, payload);
    setUsers((prev) => prev.map((u) => (u.id === id ? updated : u)));
    return updated;
  };

  const deleteUser = async (id: string) => {
    await platformUserService.deleteUser(id);
    setUsers((prev) => prev.filter((u) => u.id !== id));
    setMeta((prev) => ({ ...prev, total: Math.max(0, prev.total - 1) }));
  };

  const createRole = async (payload: CreatePlatformRoleDto) => {
    const created = await platformUserService.createRole(payload);
    await fetchUsers();
    return created;
  };

  const updateRole = async (id: string, payload: UpdatePlatformRoleDto) => {
    const updated = await platformUserService.updateRole(id, payload);
    setRoleCatalog((prev) =>
      prev.map((role) => (role.id === id ? updated : role)),
    );
    return updated;
  };

  const deleteRole = async (id: string) => {
    await platformUserService.deleteRole(id);
    await fetchUsers();
  };

  return {
    users,
    loading,
    error,
    filters,
    setFilters,
    meta,
    roleCatalog,
    permissionCatalog,
    refetch: fetchUsers,
    createUser,
    updateUser,
    deleteUser,
    createRole,
    updateRole,
    deleteRole,
  };
};

export default usePlatformUsers;
